import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { api, photoUrl, errText } from "../../lib/api";
import { renderBanner } from "../../lib/bannerRender";
import { CropDialog } from "../../components/CropDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";

const SAMPLE = { name: "Maple Ridge Kennels", tagline: "Healthy, happy puppies since 2012" };

const Preview = ({ t }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let on = true;
    renderBanner({ bg: photoUrl(t.image), color: t.text_color, align: t.align, shade: t.shade, ...SAMPLE })
      .then((c) => on && setSrc(c.toDataURL("image/jpeg", 0.6))).catch(() => {});
    return () => { on = false; };
  }, [t.image, t.text_color, t.align, t.shade]);
  return <div className="rounded-lg overflow-hidden bg-slate-900 aspect-[100/13]">{src && <img src={src} alt={t.name} className="w-full h-full object-cover" />}</div>;
};

const TemplateRow = ({ tpl, onChanged }) => {
  const [t, setT] = useState(tpl);
  const [busy, setBusy] = useState(false);
  const save = () => { setBusy(true); api.put(`/admin/banner-templates/${t.id}`, t).then(() => { toast.success("Template saved"); onChanged(); }).catch((e) => toast.error(errText(e))).finally(() => setBusy(false)); };
  const del = () => { if (window.confirm(`Delete "${t.name}"?`)) api.delete(`/admin/banner-templates/${t.id}`).then(() => { toast.success("Template deleted"); onChanged(); }).catch((e) => toast.error(errText(e))); };
  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`admin-banner-template-${t.id}`}>
      <Preview t={t} />
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[160px]"><span className="text-xs font-semibold text-slate-600">Name</span>
          <Input value={t.name} maxLength={40} onChange={(e) => setT({ ...t, name: e.target.value })} className="mt-1" data-testid={`tpl-name-${t.id}`} /></label>
        <label><span className="text-xs font-semibold text-slate-600 block">Text colour</span>
          <input type="color" value={t.text_color} onChange={(e) => setT({ ...t, text_color: e.target.value.toUpperCase() })} className="mt-1 h-9 w-14 rounded-lg border border-slate-200" data-testid={`tpl-color-${t.id}`} /></label>
        <div><span className="text-xs font-semibold text-slate-600 block mb-1">Text position</span>
          <div className="flex gap-1">{["left", "center", "right"].map((a) => (
            <button key={a} type="button" onClick={() => setT({ ...t, align: a })} data-testid={`tpl-align-${a}-${t.id}`}
              className={`h-9 px-3 rounded-lg border text-xs font-semibold capitalize ${t.align === a ? "border-[#F5551E] bg-orange-50 text-[#12234A]" : "border-slate-200 text-slate-600"}`}>{a}</button>
          ))}</div></div>
        <label className="flex items-center gap-2 h-9"><Switch checked={t.shade} onCheckedChange={(v) => setT({ ...t, shade: v })} data-testid={`tpl-shade-${t.id}`} /><span className="text-xs font-semibold text-slate-600">Dark shade behind text</span></label>
        <label className="flex items-center gap-2 h-9"><Switch checked={t.active} onCheckedChange={(v) => setT({ ...t, active: v })} data-testid={`tpl-active-${t.id}`} /><span className="text-xs font-semibold text-slate-600">Shown to sellers</span></label>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={del} className="gap-1.5 text-red-600" data-testid={`tpl-delete-${t.id}`}><Trash2 size={14} /> Delete</Button>
        <Button onClick={save} disabled={busy} className="bg-[#F5551E] hover:bg-[#e04a15] gap-1.5" data-testid={`tpl-save-${t.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</Button>
      </div>
    </div>
  );
};

export function BannerTemplatesSettings() {
  const [list, setList] = useState(null);
  const [crop, setCrop] = useState(null);
  const fileRef = useRef(null);
  const load = () => api.get("/admin/banner-templates").then((r) => setList(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (f && f.type.startsWith("image/")) setCrop(URL.createObjectURL(f));
  };
  const close = () => { URL.revokeObjectURL(crop); setCrop(null); };
  const create = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "template.jpg", { type: "image/jpeg" }));
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/banner-templates", { name: `Template ${(list?.length || 0) + 1}`, image: up.path, text_color: "#FFFFFF", align: "left", shade: true, active: true });
      toast.success("Template added. Give it a name and save.");
      close(); load();
    } catch (e) { toast.error(errText(e)); }
  };
  if (!list) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5 max-w-4xl" data-testid="banner-templates-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold text-[#12234A]">Profile banner templates</h3>
          <p className="text-sm text-slate-500">Backgrounds sellers can pick for their 2000 × 260 profile banner. They add their own name, tagline and logo. Previews use sample text.</p>
        </div>
        <Button onClick={() => fileRef.current?.click()} className="bg-[#12234A] hover:bg-[#1E3A75] gap-1.5 shrink-0" data-testid="add-banner-template"><Plus size={15} /> Add template</Button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="add-banner-template-file" />
      </div>
      {list.length === 0 && <p className="text-sm text-slate-400" data-testid="banner-templates-empty">No templates yet.</p>}
      {list.map((t) => <TemplateRow key={t.id} tpl={t} onChanged={load} />)}
      <CropDialog open={!!crop} src={crop} onClose={close} aspect={100 / 13} round={false} outW={2000} outH={260} wide
        title="Crop template background" description="Choose the 2000 × 260 area to use. Leave calm space where the seller's name will go."
        saveLabel="Add template" onCropped={create} />
    </div>
  );
}
